import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { z } from "zod";
import { formFields } from "./edit_card";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "../E3StaticData/edit-form-conf";
import { useTranslation } from "react-i18next";

const filterE3TimeSeriesSchema = z.object({
  e3_static_datum_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

const filterE3TimeSeriesFields = filterE3TimeSeriesSchema.keyof().enum;

export type FilterE3TimeSeriesSchema = z.infer<typeof filterE3TimeSeriesSchema>;

const assetDepFields: readonly DepField<FilterE3TimeSeriesSchema>[] = [
  {
    name: filterE3TimeSeriesFields.e3_static_datum_name_contains,
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterE3TimeSeriesFields.isin_contains,
    key: "isin",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterE3TimeSeries() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: filterE3TimeSeriesFields.e3_static_datum_name_contains,
      label: `Actif`,
      type: "asyncAutocomplete",
      base_url: `${Clients.E3}/${Tables.E3_STATIC_DATA}`,
      id_column: formFields.e3_static_datum_id,
      column_name: "name",
      col_span: 2,
      filters: makeFiltersBuilder<FilterE3TimeSeriesSchema>(
        assetDepFields,
        filterE3TimeSeriesFields.e3_static_datum_name_contains,
      ),
    },
    {
      name: filterE3TimeSeriesFields.asset_class_1_equals,
      label: t(`${Tables.E3_TIME_SERIES}.filter.asset_class_1`),
      type: "select",
      useTranslation: true,
      client: Clients.E3,
      table_name: Tables.E3_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "asset_class_1",
    },
    {
      name: filterE3TimeSeriesFields.asset_class_2_equals,
      label: t(`${Tables.E3_TIME_SERIES}.filter.asset_class_2`),
      type: "sectionSelect",
      useTranslation: true,
      useTransactionForSectionName: true,
      section_name_translation_key: "asset_class_1",
      section_name_table_name: Tables.E3_STATIC_DATA,
      client: Clients.E3,
      table_name: Tables.E3_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "asset_class_2",
      options: DEFAULT_ASSET_CLASS_OPTIONS,
    },
    {
      name: filterE3TimeSeriesFields.isin_contains,
      label: `ISIN`,
      type: "asyncAutocomplete",
      base_url: `${Clients.E3}/${Tables.E3_STATIC_DATA}`,
      id_column: formFields.e3_static_datum_id,
      column_name: "isin",
      filters: makeFiltersBuilder<FilterE3TimeSeriesSchema>(
        assetDepFields,
        filterE3TimeSeriesFields.isin_contains,
      ),
    },
    {
      name: filterE3TimeSeriesFields.currency_contains,
      label: `Devise`,
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "date",
      type: "dateRange",
      label: "Date de valeur",
      from: { name: filterE3TimeSeriesFields.date_from },
      to: { name: filterE3TimeSeriesFields.date_to },
      col_span: 2,
    },
    {
      name: "create",
      type: "dateRange",
      label: "Date de création",
      from: { name: filterE3TimeSeriesFields.create_from },
      to: { name: filterE3TimeSeriesFields.create_to },
      col_span: 2,
    },
  ];

  return (
    <FilterTable
      schema={filterE3TimeSeriesSchema}
      client={Clients.E3}
      tableName={Tables.E3_TIME_SERIES}
      fields={fields}
      columns={2}
    />
  );
}
